import { z } from 'zod';
import { zLinklyResult } from '../enums';

/**
 * Linkly API uses a callback pattern where Linkly server calls back to our server with data requested.
 */
export const linklySessionSchema = z.object({
  /**
   * Maps to Linkly Session ID
   */
  uuid: z.string(),

  /**
   * The linkly device that initialed the session.
   * The restaurant is associated with the device.
   */
  linklyDeviceId: z.string(),

  /**
   * Reference to the payment record - one-to-one relationship.
   * Other Linkly requests such as "status" also start a new session.
   * Note: The presence of this value can be used to determine if this is a "transaction" session or not.
   */
  paymentUuid: z.string(),

  /**
   * Because it's difficult to search by `paymentUuid` alone, we also store the order id.
   * Only set for "transaction" requests.
   */
  orderId: z.string(),

  /**
   * Scheduled job ID for the 20-second timeout that triggers if no async Linkly responses
   * are received, after successfully sending the Linkly request.
   */
  noResponseTimeoutJobId: z.string().nullable().default(null),

  /**
   * Scheduled job ID for the 2-minute hard timeout for the entire session.
   */
  sessionTimeoutJobId: z.string().nullable().default(null),

  /**
   * Result of the "transaction" session
   * - APPROVED: Payment transaction was approved
   * - DECLINED: Payment transaction was declined
   * - TIMEOUT: Payment timed out (> 60 seconds)
   * - SEND_REQUEST_FAILED: The linkly request did not return 200 response
   * - RESPONSE_PARSE_ERROR: Failed to parse the response from linkly
   * - UNAUTHORIZED: The linkly device is unauthorized
   * - NOT_FOUND: Linkly server has no record of this session
   */
  result: zLinklyResult.nullable().default(null),
  resultAt: z.number().nullable().default(null),

  /**
   * Purchase or refund amount in cents.
   * Note: Certain amounts trigger special testing behavior.
   *
   * While this could be retrieved from the order via `linklySession.orderId`
   * and `linklySession.paymentUuid`, keeping it explicit here:
   * - Reduces complexity
   * - Simplifies testing with special amount values
   */
  paymentAmount: z.number(),

  _creationTime: z.number().optional(),
  _id: z.string().optional(),
});
export type LinklySession = z.output<typeof linklySessionSchema>;

export const linklySessionStrictSchema = linklySessionSchema.safeExtend({
  paymentUuid: z.guid(),
});

export const linklySessionCvxSchema = linklySessionSchema.safeExtend({
  _creationTime: z.number(),
  _id: z.string(),
});
export type LinklySessionCvx = z.output<typeof linklySessionCvxSchema>;
